import { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Breaking news ticker — combines two sources:
 *  1. `/api/public/breaking` — articles explicitly flagged as breaking by
 *     the editorial workflow.
 *  2. `/api/public/modules/breaking-news` — free-form items the admin enters
 *     in the "Vijesti u prekidu" admin module and toggles "Javno".
 *
 * Module items take precedence (shown first). Clicking a module item with a
 * configured URL follows that link; otherwise the ticker degrades to a
 * non-link `<span>` so mis-typed data never crashes navigation.
 */
export default function BreakingTicker() {
  const [articles, setArticles] = useState([]);
  const [moduleItems, setModuleItems] = useState([]);
  const [color, setColor] = useState('red');

  useEffect(() => {
    fetch(`${API}/public/breaking`).then(r => r.json()).then(d => setArticles(d.articles || [])).catch(() => {});
    fetch(`${API}/public/modules/breaking-news`).then(r => r.ok ? r.json() : null).then(d => {
      const its = (d?.items || []);
      setModuleItems(its);
      // If the first module item declares a color, use it for the ticker bg.
      const firstColor = its[0]?.data?.color;
      if (firstColor && ['red', 'amber', 'blue'].includes(firstColor)) setColor(firstColor);
    }).catch(() => {});
  }, []);

  const entries = useMemo(() => {
    const fromModule = moduleItems.map(m => ({
      key: `m-${m.id}`,
      label: m.title,
      href: m.data?.url || null,
    }));
    const fromArticles = articles.map(a => ({
      key: `a-${a.id}`,
      label: a.title_hr,
      href: `/clanak/${a.slug}`,
    }));
    return [...fromModule, ...fromArticles];
  }, [moduleItems, articles]);

  if (entries.length === 0) return null;

  // Duplicate once for seamless CSS marquee loop.
  const items = [...entries, ...entries];

  const colorClasses = {
    red: 'bg-red-600 text-white',
    amber: 'bg-amber-500 text-black',
    blue: 'bg-blue-600 text-white',
  }[color] || 'bg-red-600 text-white';

  return (
    <div className={`${colorClasses} overflow-hidden`} data-testid="breaking-ticker">
      <div className="max-w-7xl mx-auto px-4 flex items-center h-8">
        <span className="shrink-0 bg-white dark:bg-[#0b1220] text-red-600 text-[10px] font-black uppercase px-2 py-0.5 rounded mr-3 tracking-wider">UDARNO</span>
        <div className="overflow-hidden flex-1 relative">
          <div className="ticker-track flex gap-12 whitespace-nowrap">
            {items.map((e, i) => (
              e.href ? (
                <Link
                  key={`${e.key}-${i}`}
                  to={e.href.startsWith('http') ? e.href : e.href}
                  target={e.href.startsWith('http') ? '_blank' : undefined}
                  rel={e.href.startsWith('http') ? 'noopener noreferrer' : undefined}
                  className="text-xs font-medium hover:underline shrink-0"
                  data-testid={`breaking-ticker-item-${i}`}
                >
                  {e.label}
                </Link>
              ) : (
                <span key={`${e.key}-${i}`} className="text-xs font-medium shrink-0" data-testid={`breaking-ticker-item-${i}`}>
                  {e.label}
                </span>
              )
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
